/**
 * Workflow CLI API Server
 * 提供 RESTful API 和 SSE 支持
 */

import express, { Express } from 'express';
import cors from 'cors';
import { EventEmitter } from 'events';
import { createServer, Server } from 'http';
import { logger } from '../utils/logger.js';
import { taskRouter } from './routes/tasks.js';
import { workflowRouter } from './routes/workflows.js';
import { sseRouter } from './routes/sse.js';
import { projectRouter } from './routes/project.js';
import { tagRouter } from './routes/tags.js';
import { prdRouter } from './routes/prds.js';
import { agentRouter } from './routes/agents.js';
import { promptRouter } from './routes/prompts.js';
import { cliRouter } from './routes/cli.js';
import { flowRouter } from './routes/flows.js';
import { docsRouter } from './routes/docs.js';
import { documentsRouter } from './routes/documents.js';
import { createAIRouter } from './routes/ai.js';
import { authRouter } from './routes/auth.js';
import { errorHandler } from './middleware/error-handler.js';
import { responseWrapper } from './middleware/response-wrapper.js';
import { versionMiddleware } from './middleware/version.js';
import { apiConfig } from './config.js';
import { createGraphQLServer } from './graphql/server.js';
import { getRealtimeSyncManager } from '../core/realtime-sync.js';
import type { ApiServerOptions } from './types.js';

export class ApiServer extends EventEmitter {
  private app: Express;
  private server?: Server;
  private isRunning = false;
  private port: number;
  private basePath: string;
  private version: string;
  private enableGraphQL: boolean;

  constructor(options: ApiServerOptions = {}) {
    super();
    this.port = options.port || apiConfig.port;
    this.basePath = options.basePath || apiConfig.basePath;
    this.version = options.version || apiConfig.version;
    this.enableGraphQL = options.enableGraphQL !== false;
    this.app = this.createApp();
  }

  private createApp(): Express {
    const app = express();

    // 基础中间件
    app.use(cors({
      origin: true,
      credentials: true,
      methods: ['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'OPTIONS'],
      allowedHeaders: ['Content-Type', 'Authorization', 'X-API-Version'],
    }));
    app.use(express.json());
    app.use(express.urlencoded({ extended: true }));
    
    // 自定义中间件
    app.use(responseWrapper);
    app.use(versionMiddleware);

    // 健康检查
    app.get('/health', (req, res) => {
      res.json({
        status: 'ok',
        version: this.version,
        timestamp: new Date().toISOString(),
      });
    });

    // API 路由（版本化）
    const versionPath = `${this.basePath}/${this.version}`;
    app.use(`${versionPath}/tasks`, taskRouter);
    app.use(`${versionPath}/workflows`, workflowRouter);
    app.use(`${versionPath}/sse`, sseRouter);
    app.use(`${versionPath}/project`, projectRouter);
    app.use(`${versionPath}/tags`, tagRouter);
    app.use(`${versionPath}/prds`, prdRouter);
    app.use(`${versionPath}/agents`, agentRouter);
    app.use(`${versionPath}/prompts`, promptRouter);
    app.use(`${versionPath}/flows`, flowRouter);
    app.use(`${versionPath}/docs`, docsRouter);
    app.use(`${versionPath}/documents`, documentsRouter);
    app.use(`${versionPath}/ai`, createAIRouter());
    app.use(`${versionPath}/auth`, authRouter);

    // 向后兼容路由
    app.use(`${this.basePath}/tasks`, taskRouter);
    app.use(`${this.basePath}/workflows`, workflowRouter);
    app.use(`${this.basePath}/sse`, sseRouter);
    app.use(`${this.basePath}/project`, projectRouter);
    app.use(`${this.basePath}/tags`, tagRouter);
    app.use(`${this.basePath}/prds`, prdRouter);
    app.use(`${this.basePath}/agents`, agentRouter);
    app.use(`${this.basePath}/prompts`, promptRouter);
    app.use(`${this.basePath}/flows`, flowRouter);
    app.use(`${this.basePath}/docs`, docsRouter);
    app.use(`${this.basePath}/documents`, documentsRouter);
    app.use(`${this.basePath}/ai`, createAIRouter());
    app.use(`${this.basePath}/auth`, authRouter);
    
    // CLI 路由（不需要版本化，直接在根路径）
    app.use('/cli', cliRouter);

    // 错误处理
    app.use(errorHandler);

    return app;
  }

  async start(): Promise<void> {
    if (this.isRunning) {
      logger.warn('API server is already running');
      return;
    }

    return new Promise(async (resolve, reject) => {
      try {
        // 如果启用了 GraphQL，设置 GraphQL 服务器
        if (this.enableGraphQL) {
          const graphqlServer = await createGraphQLServer(this.app);
          this.server = graphqlServer.httpServer;
        } else {
          this.server = createServer(this.app);
        }
        
        this.server.listen(this.port, () => {
          this.isRunning = true;
          
          // 设置 WebSocket 服务器
          const realtimeSync = getRealtimeSyncManager();
          realtimeSync.setupWebSocketServer(this.server);
          
          logger.info(`🚀 Workflow API Server running on http://localhost:${this.port}`);
          logger.info(`📍 Base path: ${this.basePath}/${this.version}`);
          logger.info(`🔌 WebSocket endpoint: ws://localhost:${this.port}/ws`);
          if (this.enableGraphQL) {
            logger.info(`🎯 GraphQL endpoint: http://localhost:${this.port}/graphql`);
            logger.info(`🔌 GraphQL subscriptions: ws://localhost:${this.port}/graphql`);
          }
          this.emit('started', { port: this.port });
          resolve();
        });

        this.server.on('error', (error: any) => {
          if (error.code === 'EADDRINUSE') {
            logger.error(`Port ${this.port} is already in use`);
          } else {
            logger.error('Server error:', error);
          }
          reject(error);
        });
      } catch (error) {
        reject(error);
      }
    });
  }

  async stop(): Promise<void> {
    if (!this.isRunning || !this.server) {
      logger.warn('API server is not running');
      return;
    }

    return new Promise((resolve) => {
      this.server!.close(() => {
        this.isRunning = false;
        logger.info('API server stopped');
        this.emit('stopped');
        resolve();
      });
    });
  }

  getApp(): Express {
    return this.app;
  }

  isServerRunning(): boolean {
    return this.isRunning;
  }

  getPort(): number {
    return this.port;
  }
}

// 单例实例
let serverInstance: ApiServer | null = null;

export function getApiServer(options?: ApiServerOptions): ApiServer {
  if (!serverInstance) {
    serverInstance = new ApiServer(options);
  }
  return serverInstance;
}

export function resetApiServer(): void {
  if (serverInstance?.isServerRunning()) {
    serverInstance.stop();
  }
  serverInstance = null;
}